import React, { useState, useRef } from 'react';
import { Box, Pagination, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getMediaPath } from '../../app/functions';
import link_arrow from '../../assets/icons/slider-arrow-blue.svg';

const StyledBenefitsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: '20px',
  padding: '20px 0',
  width: '100%',
  '& > *': {
    maxWidth: '100%',
    width: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .benefit-wrapper': {
    maxWidth: '100%',
    width: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '18px',
  },
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    padding: '16px 0',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  '&.single-benefit': {
    gridTemplateColumns: '1fr',
    justifyContent: 'center',
    maxWidth: 'calc((100% - 80px) / 5)',
    margin: '0 auto',
    '& > *': {
      maxWidth: '100%',
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      maxWidth: 'calc((100% - 54px) / 4)',
    },
    '@media (max-width: 1200px)': {
      maxWidth: 'calc((100% - 32px) / 3)',
    },
  },
}));

const MobileGrid = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    width: '100%',
    padding: '16px 0',
    '& > *': {
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
    },
    '& .benefit-wrapper': {
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    '&.single-benefit': {
      gridTemplateColumns: '1fr',
      justifyContent: 'center',
      maxWidth: 'calc(100% - 32px)',
      margin: '0 auto',
      '& > *': {
        maxWidth: '100%',
        width: '100%',
      },
    },
  },
}));

const MobileCarousel = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '400px',
    margin: '0 auto',
    overflow: 'hidden',
    touchAction: 'pan-y',
    position: 'relative',
    '& .single-benefit': {
      justifyContent: 'center',
      alignItems: 'center',
      maxWidth: '100%',
      width: '100%',
      '& > *': {
        maxWidth: '100%',
        width: '100%',
        transform: 'none',
        transformOrigin: 'center',
      },
    },
  },
}));

const CarouselWrapper = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    position: 'relative',
    padding: '0 30px',
    boxSizing: 'border-box',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 30px',
  },
}));

const SwipeableContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  transition: 'transform 0.3s ease-in-out',
  touchAction: 'pan-x pan-y',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
  '& > *': {
    flex: '0 0 100%',
    marginRight: '0px',
  },
  '&.single-benefit': {
    justifyContent: 'center',
    alignItems: 'center',
    '& > *': {
      maxWidth: '100%',
      width: '100%',
    },
  },
}));

const CarouselPagination = styled(Pagination)(({ theme }) => ({
  marginTop: '15px',
  '& .MuiPaginationItem-root': {
    color: 'transparent',
    minWidth: '10px',
    height: '10px',
    borderRadius: '50%',
    padding: '0',
    margin: '0 5px',
    backgroundColor: 'transparent',
    border: '1px solid #8533eb',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  '& .Mui-selected': {
    backgroundColor: '#8d4dd0',
    border: '1px solid #8d4dd0',
    '&:hover': {
      backgroundColor: '#8d4dd0',
    },
  },
  '& .MuiPaginationItem-previousNext': {
    display: 'none',
  },
  '& .MuiPaginationItem-firstLast': {
    display: 'none',
  },
}));

const NavigationArrow = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  width: '40px',
  height: '40px',
  backgroundColor: '#9e9e9e',
  borderRadius: '50%',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#757575',
  },
  '& img': {
    width: '16px',
    height: '16px',
    filter: 'brightness(0) invert(1)',
  },
}));

const PrevArrow = styled(NavigationArrow)(({ theme }) => ({
  left: '-20px',
  transform: 'translateY(-50%) rotate(180deg)',
}));

const NextArrow = styled(NavigationArrow)(({ theme }) => ({
  right: '-20px',
}));


const BenefitsGrid = ({ children, className, benefits = [], isSlider = false, ...props }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const carouselRef = useRef(null);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < benefits.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    carouselRef.current.startX = touch.clientX;
    carouselRef.current.startY = touch.clientY;
    carouselRef.current.isScrolling = false;
  };

  const handleTouchMove = (e) => {
    if (!carouselRef.current.startX) return;
    
    const touch = e.touches[0];
    const diffX = Math.abs(carouselRef.current.startX - touch.clientX);
    const diffY = Math.abs(carouselRef.current.startY - touch.clientY);
    
    // Only prevent default if it's clearly a horizontal swipe (not vertical scroll)
    if (diffX > diffY && diffX > 10) {
      e.preventDefault();
      carouselRef.current.isScrolling = false;
    } else if (diffY > diffX && diffY > 10) {
      // Allow vertical scrolling
      carouselRef.current.isScrolling = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (!carouselRef.current.startX || carouselRef.current.isScrolling) {
      carouselRef.current.startX = null;
      carouselRef.current.startY = null;
      carouselRef.current.isScrolling = false;
      return;
    }
    
    const touch = e.changedTouches[0];
    const diffX = touch.clientX - carouselRef.current.startX;
    const threshold = 50;
    
    if (Math.abs(diffX) > threshold) {
      if (diffX > 0 && currentPage < benefits.length) {
        setCurrentPage(currentPage + 1);
      } else if (diffX < 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    }
    
    carouselRef.current.startX = null;
    carouselRef.current.startY = null;
    carouselRef.current.isScrolling = false;
  };

  const isSingleBenefit = benefits.length === 1;
  const showCarousel = benefits.length > 0 && isSlider;
  const showArrows = benefits.length > 1 && isSlider;

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '1400px', 
      margin: '0 auto', 
      padding: '0 20px',
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <StyledBenefitsGrid className={className} {...props}>
        {children}
      </StyledBenefitsGrid>
      
      {!isSlider && (
        <MobileGrid className={isSingleBenefit ? 'single-benefit' : ''}>
          {benefits}
        </MobileGrid>
      )}
      
      {showCarousel && (
        <CarouselWrapper>
          {showArrows && currentPage > 1 && (
            <PrevArrow onClick={handlePrev}>
              <img src={getMediaPath(link_arrow)} alt="Previous" />
            </PrevArrow>
          )}
          <MobileCarousel>
            <SwipeableContainer
              ref={carouselRef}
              className={isSingleBenefit ? 'single-benefit' : ''}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {isSingleBenefit ? (
                benefits[0]
              ) : (
                benefits.slice(currentPage - 1, currentPage + 1)
              )}
            </SwipeableContainer>
            {showArrows && (
              <CarouselPagination
                count={benefits.length}
                page={currentPage}
                onChange={handlePageChange}
                hidePrevButton
                hideNextButton
                showFirstButton={false}
                showLastButton={false}
                size="small"
              />
            )}
          </MobileCarousel>
          
          {showArrows && currentPage < benefits.length && (
            <NextArrow onClick={handleNext}>
              <img src={getMediaPath(link_arrow)} alt="Next" />
            </NextArrow>
          )}
        </CarouselWrapper>
      )}
    </Box>
  );
};

export default BenefitsGrid;

